import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { LoadingState } from '@veilstream/ui'
import { useAuth } from '../contexts/AuthContext'
import { useProjects } from '../contexts/ProjectContext'
import { updateLastSection, type AppSection } from '../utils/appSettings'
import { useOrganizationSettings } from '../hooks/useOrganizationSettings'
import { homeRedirectPath, withLocationSuffix } from '../utils/homeRedirect'
import { needsTermsAcceptance, termsRedirectPath } from '../utils/publicRoutes'

function homeDestination(
  isLoading: boolean,
  isAuthenticated: boolean,
  user: { terms_accepted?: boolean } | null | undefined,
  projects: Parameters<typeof homeRedirectPath>[0],
  hasFetched: boolean,
  canCreateProject: boolean,
  search: string,
  hash: string,
): { to: string; fromLocation?: boolean } {
  if (isLoading) {
    return { to: withLocationSuffix('/projects', search, hash) }
  }
  if (!isAuthenticated) {
    return { to: search ? `/login${search}` : '/login', fromLocation: true }
  }
  if (needsTermsAcceptance(user, '/')) {
    return { to: termsRedirectPath(search) }
  }
  return { to: withLocationSuffix(homeRedirectPath(projects, hasFetched, canCreateProject), search, hash) }
}

/**
 * `/` is never a page. Declarative replace-navigate so the URL cannot sit on a
 * blank shell (useLayoutEffect + navigate can no-op in CI and leave only a spinner).
 */
export function HomeRedirect() {
  const { isAuthenticated, isLoading, user } = useAuth()
  const { projects, hasFetched } = useProjects()
  const { canEditProjects, loading: orgSettingsLoading } = useOrganizationSettings()
  const location = useLocation()
  const dest = homeDestination(
    isLoading,
    isAuthenticated,
    user,
    projects,
    hasFetched && !orgSettingsLoading,
    canEditProjects,
    location.search,
    location.hash,
  )
  const here = `${location.pathname}${location.search}${location.hash}`
  if (dest.to === here) {
    return <LoadingState minHeight="50vh" size="large" />
  }
  return (
    <Navigate
      to={dest.to}
      replace
      state={
        dest.fromLocation
          ? { from: { pathname: location.pathname, search: location.search, hash: location.hash } }
          : undefined
      }
    />
  )
}

/**
 * Syncs current pathname to app settings so we know if user is in projects or databases.
 * Renders nothing; run once inside Router.
 */
export function LocationSettingsSync() {
  const { pathname } = useLocation()

  useEffect(() => {
    const section: AppSection | null =
      pathname.startsWith('/projects') || pathname.startsWith('/project') || pathname.startsWith('/onboarding')
        ? 'projects'
        : pathname.startsWith('/databases') || pathname.startsWith('/database')
          ? 'databases'
          : null
    if (section) {
      updateLastSection(section)
    }
  }, [pathname])

  return null
}
